import type {
  C_ProductInfo,
  ImageGroup,
  ProductHitWithSwatches,
} from "../../lib/app.types";

const MEDIA = {
  plp: ["$plp_pwa_desktop$", ""] as const,
  pdp: ["$pdp_desktop_pwa$", ""] as const,
  thumb: ["$pdp_thumb$", ""] as const,
} as const;

export const getImageGroup = (
  imagesGroups: ImageGroup[] | undefined,
  viewType?: "swatch" | "large",
  variationValues?: {
    [key: string]: any;
  },
) => {
  // Most specific group whose EVERY variation attribute matches; same pick as SFCC/Constructor.
  return imagesGroups
    ?.filter(
      imagesGroup =>
        imagesGroup.viewType === viewType &&
        imagesGroup.variationAttributes?.every(attr =>
          attr.values?.some(val => val.value === variationValues?.[attr.id]),
        ),
    )
    .sort(
      (a, b) =>
        (b.variationAttributes?.length ?? 0) -
        (a.variationAttributes?.length ?? 0),
    )[0];
};

/**
 * Takes only the fields it reads, not a whole `C_ProductInfo` (whose required
 * `c_imageGroups` comes from an undeployed OCAPI hook), so narrower products
 * like the plan-selection `PlanProduct` pass without a cast.
 */
export const getVariantImage = (
  product: {
    c_imageGroups?: ImageGroup[];
    imageGroups?: ImageGroup[];
    variationValues?: Record<string, unknown>;
    variants?: Array<{
      orderable?: boolean;
      variationValues?: Record<string, unknown>;
    }>;
  },
  selection?: { [key: string]: string },
  type: "large" | "set" | "swatch" = "large",
) => {
  // Use c_imageGroups (from OCAPI hook) or fall back to native SCAPI imageGroups
  const imageGroups =
    (product.c_imageGroups?.length ?? 0) > 0
      ? product.c_imageGroups
      : product.imageGroups;

  if (!imageGroups || imageGroups.length === 0) return;

  // No selection passed: a variant (cart line, saved SKU) is its own selection,
  // and a bare master shows its first orderable variant, as the PDP opens on.
  const variant =
    selection ??
    product.variationValues ??
    product.variants?.find(v => v.orderable !== false)?.variationValues;
  if (!variant || Object.keys(variant).length === 0) {
    return imageGroups.filter(img => img.viewType === type)?.[0]?.images;
  }

  const resolvedType = type === "set" ? "large" : type;
  const imageGroup = getImageGroup(imageGroups, resolvedType, variant);

  // ET-296: telehealth imageGroups carry no per-variant `variationAttributes`
  // (verified against the live weight-loss catalog), so the selection lookup
  // above always misses and the caller rendered no image at all — the missing
  // mini-cart thumbnail. Fall back to a group of the same view type. Only
  // reachable once the filtered lookup has already produced nothing, so a core
  // product with real per-variant groups still gets its variant-specific image.
  //
  // Prefer the UNQUALIFIED group (the one carrying no `variationAttributes`)
  // over whatever SCAPI happened to return first. A core master mixes both
  // kinds — `26048m` returns 4 large groups, 3 of them variant-qualified — so
  // an index-0 fallback would show one specific variant's photo on a line for
  // some OTHER variant. A wrong product image in a cart row is worse than a
  // blank one; only drop to `[0]` when there is no unqualified group at all.
  // Treat a matched-but-EMPTY `images` as a miss too. `??` alone wouldn't:
  // `[]` isn't nullish, so a qualified group with no images would return `[]`
  // and render nothing — the same blank thumbnail this change exists to fix —
  // while a perfectly good unqualified group sat next to it. The sibling PDP
  // lookups guard the same way (`product-images.tsx:551,560`).
  const groupsOfType = imageGroups.filter(img => img.viewType === resolvedType);
  return (
    (imageGroup?.images?.length ? imageGroup.images : undefined) ??
    (groupsOfType.find(g => !g.variationAttributes?.length) ?? groupsOfType[0])
      ?.images
  );
};

/**
 * Returns the first "large" image from a ProductHitWithSwatches.
 *
 * Used by client-side recs (CIO → SCAPI batch fetch) where productsWithSwatches
 * sets c_imageGroups:[] (no OCAPI hooks deployed) and the hit has no top-level
 * `image` field, so the standard tile image lookups come up empty. Falls back
 * to native SCAPI `imageGroups` via getVariantImage on c_productInfo.
 */
export const getProductHitImage = (hit: ProductHitWithSwatches) =>
  getVariantImage(hit.c_productInfo as C_ProductInfo)?.[0];
